/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/react-in-jsx-scope */
import { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import Card, { CardActions, CardBody, CardHeader } from '../../components/bootstrap/Card';
import Button from '../../components/bootstrap/Button';
import tspApi from '../../API/tsp/tspApi';
import Popovers from '../../components/bootstrap/Popovers';
// import { DateRange, Range } from 'react-date-range';
import useDarkMode from '../../hooks/useDarkMode';
import { enUS } from 'date-fns/locale';
import PaginationButtons, { dataPagination, PER_COUNT } from '../../components/PaginationButtons';
import Breadcrumb from '../../components/bootstrap/Breadcrumb';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import { Calendar } from 'react-date-range';

const MIN_ROWS = 10;

interface Props {
	isFluid?: boolean;
}

const TSPWardDetails: FC<Props> = ({ isFluid }) => {
	const [params] = useSearchParams();
	const { themeStatus } = useDarkMode();

	const wardNumber = Number(params.get('wardNumber'));
	const city = params.get('city') || '';
	const dateParam = params.get('date');

	const today = new Date();

	// const [range, setRange] = useState<Range[]>([
	// {
	// 	startDate: today,
	// 	endDate: today,
	// 	key: 'selection',
	// },
	// ]);

	const [selectedDate, setSelectedDate] = useState<Date>(() => {
		return dateParam ? dayjs(dateParam).toDate() : today;
	});

	const [loading, setLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(PER_COUNT['10']);
	const [data, setData] = useState<any[]>([]);
	const [previewImg, setPreviewImg] = useState<string | null>(null);

	useEffect(() => {
		if (!wardNumber || !city || !selectedDate) return;


		const fetchData = async () => {
			setLoading(true);
			try {
				const formattedDate = dayjs(selectedDate).format('DD/MM/YYYY');
				const res = await tspApi.tspWardDetailsApi(wardNumber, city, formattedDate);
				setData(res?.data || []);
			} catch {
				setData([]);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [wardNumber, city, selectedDate]);

	const truncateText = (text: string, max = 25) =>
		text && text.length > max ? `${text.slice(0, max)}...` : text || 'N/A';

	const getBagNumber = (bags: any, key: string) => {
		if (!bags || typeof bags !== 'object') return 0;
		const val = Number(bags[key]);
		return isNaN(val) ? 0 : val;
	};

	const getTotalBags = (bags: any) =>
		getBagNumber(bags, 'dry') +
		getBagNumber(bags, 'wet') +
		getBagNumber(bags, 'bioMedical') +
		getBagNumber(bags, 'hazardous') +
		getBagNumber(bags, 'mixed');

	const getImageUrl = (imgs?: Record<string, string>): string | null => {
		if (!imgs) return null;
		const values = Object.values(imgs);
		return values.length ? values[0] : null;
	};

	const datePicker = (closePopover: () => void) => {
	const handleReset = () => {
		setSelectedDate(today);
	};

	const handleApply = () => {
		closePopover();
	};

	return (
		<div
		style={{
			padding: '12px',
			minWidth: '360px',
			backgroundColor: '#fff',
			borderRadius: '12px',
			boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
			position: 'absolute',

			/* 👇 keeps old alignment */
			left: '-375px',
			right: '20px',
		}}
		>
		{/* CENTERED CALENDAR */}
		<div
			style={{
			display: 'flex',
			justifyContent: 'center',
			}}
		>
			<Calendar
			date={selectedDate}
			onChange={(date: Date) => setSelectedDate(date)}
			maxDate={today}
			locale={enUS}
			color="#4f46e5"
			/>
		</div>

		{/* DIVIDER */}
		<div
			style={{
			height: 1,
			background: '#E5E7EB',
			margin: '10px 0',
			}}
		/>

		{/* ACTION BUTTONS */}
		<div
			style={{
			display: 'flex',
			gap: 8,
			}}
		>
			<Button
			color="light"
			style={{ flex: 1, padding: '6px 0' }}
			onClick={handleReset}
			>
			RESET
			</Button>

			<Button
			color="primary"
			style={{ flex: 1, padding: '6px 0' }}
			onClick={handleApply}
			>
			APPLY
			</Button>
		</div>
		</div>
	);
	};

  const FancyLoader = () => {
    return (
      <div className="fancy-loader-wrapper">
        <span className="dot"></span>
        <span className="dot"></span>
        <span className="dot"></span>

        <style>
          {`
          .fancy-loader-wrapper{
            display:flex;
            justify-content:center;
            align-items:center;
            gap:10px;
            padding:40px 0;
          }

          .dot{
            width:10px;
            height:10px;
            border-radius:50%;
            background:#0d6efd;
            animation:bounce 1.4s infinite ease-in-out both;
          }

          .dot:nth-child(1){
            animation-delay:-0.32s;
          }
          .dot:nth-child(2){
            animation-delay:-0.16s;
          }

          @keyframes bounce{
            0%,80%,100%{
              transform:scale(0);
              opacity:0.4;
            }
            40%{
              transform:scale(1);
              opacity:1;
            }
          }
        `}
        </style>
      </div>
    );
  };

	return (
		<>
			<PageWrapper title='TSP List'>
				<SubHeader>
					<SubHeaderLeft>
						<Breadcrumb
							list={[
								{ title: 'TSP List', to: '/fleet/tsp-list' },
								{ title: 'TSP Ward Details', to: '/' },
							]}
						/>
					</SubHeaderLeft>
				</SubHeader>

				<Card stretch={isFluid} style={{ height: '80vh' }}>
					<CardHeader>
						<div className='fw-bold fs-5'>
							Ward {wardNumber} – TSP Details ({city})
						</div>

						<CardActions>
						<Popovers
						trigger="click"
						placement="bottom-end"
						desc={(closePopover) => datePicker(closePopover)}
						>
						<Button color="info" isLight>
							{dayjs(selectedDate).format('DD MMM, YYYY')}
						</Button>
						</Popovers>

						</CardActions>
					</CardHeader>

					<CardBody className='table-responsive'>
						<table className='table table-bordered table-hover align-middle text-center'>
							<thead className='table-primary'>
								<tr>
									<th rowSpan={2}>TSP</th>
									<th rowSpan={2}>Cleaned</th>
									<th rowSpan={2}>TSP Name</th>
									<th rowSpan={2}>Time</th>
									<th rowSpan={2}>Vehicle</th>
									<th rowSpan={2}>Image Uploaded</th>
									<th colSpan={6}>No Of Bags</th>
								</tr>
								<tr>
									<th>Dry</th>
									<th>Wet</th>
									<th>Hazardous</th>
									<th>Bio Medical</th>
									<th>Mixed</th>
									<th>Total</th>
								</tr>
							</thead>

							<tbody>
								{loading && (
									<tr>
										<td colSpan={12}>
											<FancyLoader />
										</td>
									</tr>
								)}

								{!loading &&
									dataPagination(data, currentPage, perPage).map((item, i) => {
										const imageUrl = getImageUrl(item.imgs);

										return (
											<tr key={i}>
												<td>{item.tsp}</td>
												<td>
													<span
														className={`badge ${
															item.isScan ? 'bg-success' : 'bg-danger'
														}`}>
														{item.isScan ? 'Yes' : 'No'}
													</span>
												</td>
												<td className='text-start'>
													{truncateText(item.propertyName)}
												</td>
												<td>{item.time || 'N/A'}</td>
												<td>{item.vehiclID || 'N/A'}</td>
												<td>
													{imageUrl ? (
														<img
															src={imageUrl}
															alt='Uploaded'
															style={{
																width: 40,
																height: 40,
																borderRadius: 6,
																objectFit: 'cover',
																cursor: 'pointer',
															}}
															onClick={() => setPreviewImg(imageUrl)}
														/>
													) : (
														<span className='text-muted'>N/A</span>
													)}
												</td>

												<td>{getBagNumber(item.bags, 'dry')}</td>
												<td>{getBagNumber(item.bags, 'wet')}</td>
												<td>{getBagNumber(item.bags, 'hazardous')}</td>
												<td>{getBagNumber(item.bags, 'bioMedical')}</td>
												<td>{getBagNumber(item.bags, 'mixed')}</td>
												<td className='fw-bold'>
													{getTotalBags(item.bags)}
												</td>
											</tr>
										);
									})}
							</tbody>
						</table>
					</CardBody>

					<div className="d-flex justify-content-between align-items-center p-3">
					<Button
						color="info"
						isOutline
						size="md"
						className="px-4 fw-medium ms-3"
						onClick={() => window.history.back()}
					>
						Back
					</Button>

					<PaginationButtons
						data={data}
						label="records"
						currentPage={currentPage}
						setCurrentPage={setCurrentPage}
						perPage={perPage}
						setPerPage={setPerPage}
					/>
					</div>

				</Card>
			</PageWrapper>
			{previewImg !== null && (
				<div
					className='modal fade show'
					style={{ display: 'block', background: 'rgba(0,0,0,0.6)' }}
					onClick={() => setPreviewImg(null)}>
					<div
						className='modal-dialog modal-lg modal-dialog-centered'
						onClick={(e) => e.stopPropagation()}>
						<div className='modal-content'>
							<div className='modal-header'>
								<h5 className='modal-title'>Uploaded Image</h5>
								<button className='btn-close' onClick={() => setPreviewImg(null)} />
							</div>
							<div className='modal-body text-center'>
								<img
									src={previewImg}
									alt='Preview'
									style={{ maxWidth: '100%', maxHeight: '70vh' }}
								/>
							</div>
						</div>
					</div>  
				</div>
			)}
		</>
	);
};

export default TSPWardDetails;
